/**
 * ProgramInterviews — read-only list of interview sessions for a program's
 * Capability Need Statement (and future documents).
 *
 * Listens for `agentDataChanged` events with `resource: 'interview'` to
 * refetch when the agent records answers or completes interviews. Expand
 * an interview to see its recorded answers inline.
 */
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { tokenManager } from '../../lib/api'
import Badge from '../../components/ui/Badge'
import { ChevronDown, ChevronRight, MessageCircle } from 'lucide-react'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

type InterviewStatus = 'draft' | 'dispatched' | 'in_progress' | 'complete' | 'abandoned'

interface UserBasic {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  role: string
}

/** A recorded answer — now a ProgramObservation (interview provenance). */
interface Answer {
  id: string
  interview: string
  spec: number
  spec_key: string
  spec_name: string
  value: string | number | boolean | null
  confidence: number
  observed_date: string | null
  recorded_by: number | null
  inherited_from: string | null
  source_document: number | null
  created_at: string
  updated_at: string
}

interface Interview {
  id: string
  program_document: string
  respondent: number
  respondent_detail?: UserBasic
  role_on_program: string
  status: InterviewStatus
  conversation: string | null
  dispatched_at: string | null
  started_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
  answers: Answer[]
}

interface ProgramDocument {
  id: string
  program: number
  template: string
  template_key: string
  status: string
  created_at: string
  updated_at: string
  interviews: Interview[]
}

const TEMPLATE_DISPLAY_NAMES: Record<string, string> = {
  capability_need_statement: 'Capability Need Statement',
}

const STATUS_BADGE_VARIANT: Record<InterviewStatus, 'pending' | 'warning' | 'in-progress' | 'success' | 'rejected'> = {
  draft: 'pending',
  dispatched: 'warning',
  in_progress: 'in-progress',
  complete: 'success',
  abandoned: 'rejected',
}

const STATUS_LABEL: Record<InterviewStatus, string> = {
  draft: 'Draft',
  dispatched: 'Dispatched',
  in_progress: 'In Progress',
  complete: 'Complete',
  abandoned: 'Abandoned',
}

const ROLE_LABEL: Record<string, string> = {
  pm: 'Program Manager',
  reviewer: 'Reviewer',
  legal: 'Legal',
  contracting: 'Contracting Officer',
  technical_sme: 'Technical SME',
  mission_owner: 'Mission Owner',
  end_user: 'End User',
  observer: 'Observer',
}

function formatRespondent(iv: Interview): string {
  const d = iv.respondent_detail
  if (!d) return `User #${iv.respondent}`
  return d.full_name || d.username || d.email
}

function formatRole(role: string): string {
  return ROLE_LABEL[role] || role
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

const ProgramInterviews = () => {
  const { programId } = useParams<{ programId: string }>()
  const [programDocuments, setProgramDocuments] = useState<ProgramDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const fetchInterviews = useCallback(async () => {
    if (!programId) return
    setError(null)
    const token = tokenManager.getAccessToken()
    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/documents/program-documents/?program=${programId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      )
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const payload = await resp.json()
      const results: ProgramDocument[] = Array.isArray(payload) ? payload : (payload.results || [])
      setProgramDocuments(results)
    } catch (err: any) {
      console.error('Failed to fetch program documents', err)
      setError(err.message || 'Failed to load interviews')
    } finally {
      setLoading(false)
    }
  }, [programId])

  useEffect(() => {
    fetchInterviews()
  }, [fetchInterviews])

  // Refetch when an `agentDataChanged` event reports an interview change.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.resource === 'interview') {
        console.log('🔄 Agent changed interviews, refetching...', detail)
        fetchInterviews()
      }
    }
    window.addEventListener('agentDataChanged', handler)
    return () => window.removeEventListener('agentDataChanged', handler)
  }, [fetchInterviews])

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (loading) {
    return <div className="p-8 text-muted-foreground">Loading interviews…</div>
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-4">
          {error}
        </div>
      </div>
    )
  }

  if (programDocuments.length === 0) {
    return (
      <div className="p-8">
        <div className="bg-card rounded-lg border border-border p-8 text-center">
          <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <h2 className="text-lg font-semibold text-foreground mb-1">No interview documents yet</h2>
          <p className="text-sm text-muted-foreground">
            A Capability Need Statement is auto-created with each new program. If you're seeing
            this, the program may have been created before the CNS template was seeded.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Interviews</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Structured conversations with stakeholders that build up this program's documents. The
          PM interview runs on each workflow step's page; future phases will dispatch interviews
          to SMEs and end users.
        </p>
      </div>

      {programDocuments.map((pd) => (
        <section key={pd.id} className="space-y-4">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-medium text-foreground">
              {TEMPLATE_DISPLAY_NAMES[pd.template_key] || pd.template_key}
            </h2>
            <span className="text-xs text-muted-foreground">
              Document status: <span className="font-medium text-foreground">{pd.status}</span>
            </span>
          </div>

          {pd.interviews.length === 0 ? (
            <div className="bg-card rounded-lg border border-border p-6 text-sm text-muted-foreground">
              No interviews yet. They start when you open a workflow step that gathers info.
            </div>
          ) : (
            <ul className="space-y-3">
              {pd.interviews.map((iv) => {
                const isOpen = expanded.has(iv.id)
                const answers = iv.answers ?? []
                const answeredCount = answers.length
                return (
                  <li
                    key={iv.id}
                    className="bg-card rounded-lg border border-border hover:border-primary/50 transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => toggleExpanded(iv.id)}
                      className="w-full px-6 py-4 flex items-start gap-4 text-left"
                    >
                      <div className="mt-1 text-muted-foreground">
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-medium text-foreground truncate">
                            {formatRespondent(iv)}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {formatRole(iv.role_on_program)}
                          </span>
                          <Badge variant={STATUS_BADGE_VARIANT[iv.status]}>
                            {STATUS_LABEL[iv.status]}
                          </Badge>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground flex gap-4 flex-wrap">
                          <span>{answeredCount} answer{answeredCount === 1 ? '' : 's'} recorded</span>
                          {iv.started_at && <span>Started {formatDate(iv.started_at)}</span>}
                          {iv.completed_at && <span>Completed {formatDate(iv.completed_at)}</span>}
                          {!iv.started_at && iv.dispatched_at && (
                            <span>Dispatched {formatDate(iv.dispatched_at)}</span>
                          )}
                        </div>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="border-t border-border px-6 py-4 space-y-3">
                        {answers.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No answers recorded yet.
                          </p>
                        ) : (
                          answers.map((a) => (
                            <div
                              key={a.id}
                              className="bg-muted/40 rounded-md px-4 py-3 space-y-1"
                            >
                              <div className="text-xs text-muted-foreground">
                                <span className="font-medium text-foreground">{a.spec_name || a.spec_key}</span>
                                <span className="ml-2 font-mono">{a.spec_key}</span>
                                {a.inherited_from && (
                                  <span className="ml-2">· Inherited</span>
                                )}
                                {a.source_document && (
                                  <span className="ml-2">· From document #{a.source_document}</span>
                                )}
                              </div>
                              <div className="text-sm text-foreground whitespace-pre-wrap">
                                {a.value === null || a.value === undefined ? '' : String(a.value)}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Confidence {a.confidence}/5 · {formatDate(a.observed_date)}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      ))}
    </div>
  )
}

export default ProgramInterviews
